import { useRef } from 'react';

import { useLayer } from '@coursera-web/statsig';
import { isEqual } from 'lodash';

import type { EnterpriseSearchBarConfig } from 'bundles/search-common/components/searchbar/searchBarWrapperTypes';
import { useEnterpriseOrgSkills } from 'bundles/search-common/hooks/useEnterpriseOrgSkills';
import { type AutocompleteItemGroup, createEnterpriseSkillGroups } from 'bundles/search-common/utils/autocomplete';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';

/**
 * Skill suggestions for the unified enterprise search bar, gated behind the `enable_enterprise_autocomplete_skills`
 * param of the `enterprise-search-layer` Statsig layer (SEARCH-2484).
 *
 * Suggests skills from the org in the bar's `skillsCatalogOrgSlug`, covering all of its programs. The org's skills load the
 * first time the learner types and are matched in the browser against `matchQuery`, the query the rest of the
 * suggestions are currently showing, so the skills change together with them. The layer is only read for bars that
 * carry an org, once the dropdown is open and the learner has typed, so no exposure is logged before then. Skills
 * also require the org's `enableSkillsInSearchAndBrowse` product feature, as in the legacy enterprise bar.
 */
export const useEnterpriseAutocompleteSkillGroups = (
  enterpriseSearchBarConfig: EnterpriseSearchBarConfig | undefined,
  searchText: string,
  matchQuery: string,
  isDropdownOpen: boolean
): AutocompleteItemGroup[] => {
  const enterpriseLayer = useLayer('enterprise-search-layer');
  const productFeatures = useEnterpriseProductFeatures();

  const orgSlug = enterpriseSearchBarConfig?.skillsCatalogOrgSlug;
  // Only bars that carry an org can show skills, so only those reach the layer read and log an exposure.
  //
  const isEnabled =
    !!orgSlug &&
    !!productFeatures?.enableSkillsInSearchAndBrowse &&
    isDropdownOpen &&
    searchText.trim().length > 0 &&
    enterpriseLayer.get('enable_enterprise_autocomplete_skills', false);

  const skills = useEnterpriseOrgSkills(orgSlug, matchQuery, isEnabled);
  const groups = isEnabled && orgSlug ? createEnterpriseSkillGroups(skills, orgSlug, matchQuery) : [];

  // The groups are rebuilt every render, so keep the previous ones while they are unchanged.
  const stableGroupsRef = useRef(groups);
  if (!isEqual(stableGroupsRef.current, groups)) {
    stableGroupsRef.current = groups;
  }

  return stableGroupsRef.current;
};

export default useEnterpriseAutocompleteSkillGroups;
